/**
 * @file index.tsx
 *
 * @fileoverview Renders bottom navigation sub menu.
 */
import { useContext, useMemo } from 'react';
import { MenuItem } from '~/jobiqo/types/types';
import Link from '~/jobiqo/components/Link';
import { isUrlActive } from '~/jobiqo/utils/links';
import { PathContext } from '~/jobiqo/context/app';
import {
  getMenuIcon,
  renderIcon,
} from '~/jobiqo/components/Layout/components/MenuItemWrapper';
import { BottomSubMenu } from '~/vcl/molecules/04-navigation/06-bottom-sub-menu';
import HiddenVisually from '~/vcl/atoms/14-w-a11y/02-visually-hidden';
import { MenuLink } from '~/vcl/atoms/08-navigation/01-menu-link';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { LayoutContext } from '~/jobiqo/context/layout';
import { Ripple } from '~/vcl/atoms/21-ripple';
import NextLink from 'next/link';

export interface LayoutBottomSubMenuProps {
  /**
   * The menu item.
   */
  menuItem: MenuItem;
}

export function LayoutBottomSubMenu({ menuItem }: LayoutBottomSubMenuProps) {
  const { path } = useContext(PathContext);
  const { openedBottomSubMenu, setOpenedBottomSubMenu } = useContext(
    LayoutContext,
  );
  const LinkIcon = useMemo(() => getMenuIcon(menuItem.class), [menuItem.class]);
  const collapsed = useMemo(() => openedBottomSubMenu !== menuItem.title, [
    openedBottomSubMenu,
  ]);

  return (
    <>
      <MenuLink
        onClick={() =>
          setOpenedBottomSubMenu(collapsed ? menuItem.title : undefined)
        }
      >
        <>
          <Ripple backgroundColor="primary" />
          {renderIcon(menuItem, 'bottom', LinkIcon)}
        </>
        <HiddenVisually>
          <span>{menuItem.title}</span>
        </HiddenVisually>
      </MenuLink>
      {!!menuItem.children?.length && (
        <BottomSubMenu
          collapsed={collapsed}
          onOverlayClick={() => setOpenedBottomSubMenu()}
          Items={menuItem.children.map((item) => {
            if (!item) {
              return null;
            }

            const LinkIcon = getMenuIcon(item.class);

            return (
              <Link
                asChild
                key={`${item.url.path}--${item.title}`}
                href={item.url.path}
              >
                <MenuLink
                  linkElement={NextLink}
                  href={item.url.path}
                  active={isUrlActive(item.url, path)}
                >
                  <FlexItems gap={4} alignItems="center">
                    <FlexItem>{renderIcon(item, 'bottom', LinkIcon)}</FlexItem>
                    <FlexItem>
                      <Text margin={0}>{item.title}</Text>
                    </FlexItem>
                  </FlexItems>
                </MenuLink>
              </Link>
            );
          })}
        />
      )}
    </>
  );
}
